'use client';

import React from 'react';
import styled from 'styled-components';
import { Skeleton } from './Skeleton/Skeleton';

// ============================================================================
// Styled Components
// ============================================================================

const SkeletonWrapper = styled.div`
  width: 100%;
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
`;

const SkeletonTableContainer = styled.div`
  padding: 20px;
`;

const SkeletonTableHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e5e7eb;
`;

const SkeletonTableBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SkeletonRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid #f3f4f6;
`;

const SkeletonPagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-top: 1px solid #e5e7eb;
`;

// ============================================================================
// Component
// ============================================================================

export interface DataTableSkeletonProps {
  /** Number of columns to display */
  columns?: number;
  /** Number of rows to display */
  rows?: number;
  /** Show search bar */
  showSearch?: boolean;
  /** Show filter button */
  showFilter?: boolean;
  /** Show pagination */
  showPagination?: boolean;
}

/**
 * DataTableSkeleton Component
 * 
 * Loading skeleton for DataTable component
 */
export const DataTableSkeleton: React.FC<DataTableSkeletonProps> = ({
  columns = 5,
  rows = 10,
  showPagination = true,
}) => {
  return (
    <SkeletonWrapper>

      {/* Table */}
      <SkeletonTableContainer>
        {/* Table Header */}
        <SkeletonTableHeader>
          {Array.from({ length: columns }).map((_, index) => (
            <Skeleton key={`header-${index}`} width="80%" height="16px" />
          ))}
        </SkeletonTableHeader>

        {/* Table Body */}
        <SkeletonTableBody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <SkeletonRow key={`row-${rowIndex}`}>
              {Array.from({ length: columns }).map((_, colIndex) => (
                <Skeleton 
                  key={`cell-${rowIndex}-${colIndex}`} 
                  width="90%" 
                  height="16px"
                  animation="wave"
                />
              ))}
            </SkeletonRow>
          ))}
        </SkeletonTableBody>
      </SkeletonTableContainer>

      {/* Pagination */}
      {showPagination && (
        <SkeletonPagination>
          <Skeleton width="120px" height="32px" />
          <Skeleton width="200px" height="32px" />
        </SkeletonPagination>
      )}
    </SkeletonWrapper>
  );
};

export default DataTableSkeleton;

